import { Input } from '@trm/ui-kit-admin/v7';
import { Eye, EyeOff } from '@trm/ui-kit-admin/v7/icons';
import { cx } from '@trm/ui-kit-admin/v7/utils';
import type { ComponentPropsWithRef } from 'react';
import { forwardRef } from 'react';
import '~/pages/auth/auth-shared.css';

import './OnboardingInput.css';

/**
 * Props for the OnboardingInput component
 * Extends all Input component props from @trm/ui-kit-admin/v7
 */
export interface OnboardingInputProps extends Omit<ComponentPropsWithRef<typeof Input>, 'label'> {
  /**
   * Unique identifier for the input element, used for accessibility and form handling
   */
  id?: string;
  /**
   * Label text to display above the input
   */
  label?: string;
  /**
   * Error message to display below the input
   */
  error?: string;
  /**
   * Enable password visibility toggle (Eye/EyeOff icon)
   */
  showPasswordToggle?: boolean;
  /**
   * Controlled state for password visibility
   */
  showPassword?: boolean;
  /**
   * Callback when password visibility is toggled
   */
  onTogglePassword?: () => void;
  /**
   * Additional className for the outer container
   */
  containerClassName?: string;
  /**
   * Additional className for the label
   */
  labelClassName?: string;
  /**
   * Additional className for the error message
   */
  errorClassName?: string;
}

/**
 * Shared input component for onboarding and authentication flows.
 *
 * This component provides consistent styling for input fields across:
 * - Registration forms (AuthRegisterCognitoNew)
 * - Email verification flows (EmailVerification)
 * - Password setup (SetCognitoPassword)
 * - Password reset (ForgotPassword)
 * - Login flows (LoginWithEmail, EmailAuth)
 *
 * ## Design Specifications
 *
 * - **Border Radius**: Small radius (2px) for subtle modern aesthetic
 * - **Wrapper**: Small rounded corners (!rounded-sm)
 * - **Input Element**: Small rounded corners (!rounded-sm)
 * - **Consistent Styling**: Matches the OnboardingButton squared aesthetic
 * - **Password Toggle**: Optional Eye/EyeOff icon with smart positioning
 * - **Labels**: Onboarding-specific label styling (gray-300)
 * - **Errors**: Display below input with error-primary styling
 *
 * ## Usage
 *
 * @example
 * Basic usage with label
 * ```tsx
 * <OnboardingInput
 *   label="Email"
 *   type="email"
 *   name="email"
 *   value={values.email}
 *   onChange={(value) => setFieldValue('email', value)}
 *   onBlur={handleBlur}
 * />
 * ```
 *
 * @example
 * With validation and error
 * ```tsx
 * <OnboardingInput
 *   label="Username"
 *   type="text"
 *   name="username"
 *   value={values.username}
 *   onChange={(value) => setFieldValue('username', value)}
 *   onBlur={handleBlur}
 *   isInvalid={!!(errors.username && touched.username)}
 *   error={errors.username && touched.username ? errors.username : undefined}
 * />
 * ```
 *
 * @example
 * Password field with toggle
 * ```tsx
 * <OnboardingInput
 *   label="Password"
 *   type={showPassword ? 'text' : 'password'}
 *   name="password"
 *   value={values.password}
 *   onChange={(value) => setFieldValue('password', value)}
 *   onBlur={handleBlur}
 *   showPasswordToggle
 *   showPassword={showPassword}
 *   onTogglePassword={() => setShowPassword(!showPassword)}
 *   isInvalid={!!(errors.password && touched.password)}
 *   error={errors.password && touched.password ? errors.password : undefined}
 * />
 * ```
 *  *
 * `@see` app/src/pages/auth/onboarding/components/OnboardingButton/OnboardingButton.tsx
 * `@see` packages/ui-kit/src/components/base/input/input.tsx
 */
export const OnboardingInput = forwardRef<HTMLInputElement, OnboardingInputProps>(
  (
    {
      id,
      label,
      error,
      showPasswordToggle,
      showPassword,
      onTogglePassword,
      containerClassName,
      labelClassName,
      errorClassName,
      wrapperClassName,
      inputClassName,
      isInvalid,
      ...props
    },
    ref,
  ) => {
    // Determine if we need a wrapper for label/error/password toggle
    const needsWrapper = label || error || showPasswordToggle;

    // Password toggle button positioning - adjust for error icon
    const toggleButtonPosition = isInvalid ? 'right-9' : 'right-3';

    const inputElement = (
      <Input
        id={id}
        ref={ref}
        wrapperClassName={cx(
          '!rounded-[2px] bg-[var(--auth-bg-primary-subtle)] !ring-0 border border-disabled',
          wrapperClassName,
        )}
        inputClassName={cx(
          '!rounded-[2px] bg-[var(--auth-bg-primary-subtle)]',
          // Override base Input autofill Tailwind classes
          '!autofill:rounded-[2px]',
          inputClassName,
        )}
        isInvalid={isInvalid}
        {...props}
      />
    );

    // If no wrapper needed, wrap in a simple div with the class for CSS targeting
    if (!needsWrapper) {
      return <div className="onboarding-input-wrapper">{inputElement}</div>;
    }

    return (
      <div className={cx('onboarding-input-wrapper space-y-0', containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={id}
            className={cx('mb-1.5 block text-sm font-medium text-gray-300', labelClassName)}
          >
            {label}
          </label>
        )}

        {/* Input with optional password toggle */}
        {showPasswordToggle ? (
          <div className="relative">
            {inputElement}
            <button
              type="button"
              onClick={onTogglePassword}
              className={cx(
                'absolute top-1/2 -translate-y-1/2 text-gray-500 transition-all hover:text-gray-700',
                toggleButtonPosition,
              )}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="size-4 text-blue-400" />
              ) : (
                <Eye className="size-4 text-blue-400" />
              )}
            </button>
          </div>
        ) : (
          inputElement
        )}

        {/* Error message */}
        {error && isInvalid && (
          <p className={cx('mt-1.5 text-sm text-error-primary', errorClassName)}>{error}</p>
        )}
      </div>
    );
  },
);

OnboardingInput.displayName = 'OnboardingInput';
